import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  usePlayerAIClient,
  type UserSettings,
  type UserSettingsPatch,
} from "@playbookai/player-ai-client";
import { useAuthStore } from "../auth/auth-store.js";

export function usePersonalPreferences() {
  const client = usePlayerAIClient();
  const claims = useAuthStore((s) => s.claims);
  const cache = useQueryClient();
  const key = ["player-ai-personal-settings", claims?.tenantId, claims?.userId];
  const query = useQuery({
    queryKey: key,
    queryFn: () => client.getSettings(),
    retry: false,
    refetchInterval: 10000,
  });
  const profiles = useQuery({
    queryKey: [...key, "profiles"],
    queryFn: () => client.getPersonalProfiles(),
    retry: false,
  });
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState<UserSettingsPatch | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => client.subscribeSettings((value) => cache.setQueryData(key, value)),
    [client, cache, claims?.tenantId, claims?.userId],
  );
  async function save(patch: UserSettingsPatch) {
    if (pending) return;
    setPending(true);
    setError(null);
    setFailed(null);
    try {
      const current = cache.getQueryData<UserSettings>(key);
      await client.updateSettings({
        ...patch,
        expected_version: current?.preference_version ?? 0,
      });
    } catch (e) {
      setFailed(patch);
      setError(e instanceof Error ? e.message : "Could not save preferences.");
      // Refresh authoritative version; retain the user's intended patch for an explicit retry.
      await query.refetch();
    } finally {
      setPending(false);
    }
  }
  return {
    query,
    profiles,
    pending,
    failed,
    error,
    save,
    retry: () => failed && void save(failed),
  };
}
